// markup/measure.mjs — DropPin measurement (pure, headless).
// ---------------------------------------------------------------------------
// Turns a measure object's NORMALISED points + the drawing's natural size +
// the (optional) calibration into real-world length / area readouts. Every
// number is produced by the shared engine (@insitebg/plan-markup-core): polyline
// length, shoelace area, the click-to-calibrate solve, the pt->mm / area
// conversions and the unit formatting. This module only wires them together
// and picks the matching area unit — no parallel maths.
//
// CALIBRATION is stored resolution-independently as a normalised reference
// SEGMENT + its known real length in mm ({ refA, refB, knownMm }). The
// mm-per-image-pixel constant is DERIVED here from the current natural size,
// so both the reference and the measured shapes rescale together if the plan
// is ever re-rastered — the calibration never goes stale.
// ---------------------------------------------------------------------------

import {
  distance,
  polylineLength,
  polygonArea,
  mmPerPdfPtFromKnownDistance,
  ptToMm,
  areaPtToMm2,
  formatLength,
  formatArea,
  resolveAutoDecimals,
} from '@insitebg/plan-markup-core';
import { toPixels } from './model.mjs';

// Length unit -> the area unit format.mjs expects.
export const AREA_UNIT_FOR = { mm: 'mm2', m: 'm2', ft: 'ft2' };

// Resolve a stored calibration to { mmPerImagePx, refDistPx } for a given
// natural size, or null if uncalibrated / degenerate. mmPerImagePx is the
// exact analogue of the core's mmPerPdfPt — "mm per unit of measurement
// space" — except our measurement space is natural image pixels, not PDF pts.
export function resolveCalibration(calibration, W, H) {
  if (!calibration || !W || !H) return null;
  const a = { x: calibration.refA.x * W, y: calibration.refA.y * H };
  const b = { x: calibration.refB.x * W, y: calibration.refB.y * H };
  const refDistPx = distance(a, b);
  const mmPerImagePx = mmPerPdfPtFromKnownDistance(calibration.knownMm, refDistPx);
  return mmPerImagePx ? { mmPerImagePx, refDistPx } : null;
}

// Total length of a normalised polyline. Returns { calibrated, lengthPx, mm, label }.
export function measureLength(points, W, H, calibration, unit = 'm', decimalsPref = 'auto') {
  const px = toPixels(points, W, H);
  const lengthPx = polylineLength(px);
  const cal = resolveCalibration(calibration, W, H);
  if (cal) {
    const mm = ptToMm(lengthPx, cal.mmPerImagePx);
    const dec = resolveAutoDecimals(decimalsPref, unit === 'mm');
    return { calibrated: true, lengthPx, mm, label: formatLength(mm, unit, dec) };
  }
  return { calibrated: false, lengthPx, mm: null, label: `${lengthPx.toFixed(0)} px` };
}

// Area (+ perimeter) of a normalised polygon.
export function measureArea(points, W, H, calibration, unit = 'm', decimalsPref = 'auto') {
  const px = toPixels(points, W, H);
  const areaPx = polygonArea(px);
  const perimPx = polylineLength(px) + (px.length > 2 ? distance(px[px.length - 1], px[0]) : 0);
  const cal = resolveCalibration(calibration, W, H);
  const areaUnit = AREA_UNIT_FOR[unit] || 'm2';
  if (cal) {
    const mm2 = areaPtToMm2(areaPx, cal.mmPerImagePx);
    const perimMm = ptToMm(perimPx, cal.mmPerImagePx);
    const decLen = resolveAutoDecimals(decimalsPref, unit === 'mm');
    return {
      calibrated: true,
      areaPx, perimPx, mm2, perimMm,
      areaLabel: formatArea(mm2, areaUnit, decLen),
      perimLabel: formatLength(perimMm, unit, decLen),
    };
  }
  return {
    calibrated: false,
    areaPx, perimPx, mm2: null, perimMm: null,
    areaLabel: `${areaPx.toFixed(0)} px²`,
    perimLabel: `${perimPx.toFixed(0)} px`,
  };
}

// Measure a whole measure object (dispatches on measureKind).
export function measureObject(o, W, H, calibration, unit = 'm', decimalsPref = 'auto') {
  if (o.measureKind === 'area') {
    return { kind: 'area', ...measureArea(o.points, W, H, calibration, unit, decimalsPref) };
  }
  return { kind: 'length', ...measureLength(o.points, W, H, calibration, unit, decimalsPref) };
}

// Build a calibration record from a drawn reference line + a known real length.
// knownMm must already be millimetres (the dialog converts m / ft / mm -> mm).
export function makeCalibration(refA, refB, knownMm) {
  if (!(knownMm > 0)) return null;
  return { refA: { x: refA.x, y: refA.y }, refB: { x: refB.x, y: refB.y }, knownMm };
}
